import { X, Sparkles } from 'lucide-react';
import type { SubscriberRow } from './types';
import { mockSelectionInsights } from '../../lib/subscriberAiMock';
import { channelLabel, type ChannelRow } from '../../lib/channels';
import { subscriberRoleLabel } from '../../lib/subscriberRoles';

interface SubscriberSelectionInsightsModalProps {
  isOpen: boolean;
  onClose: () => void;
  selectedRows: SubscriberRow[];
  channelCatalog: ChannelRow[];
}

export default function SubscriberSelectionInsightsModal({
  isOpen,
  onClose,
  selectedRows,
  channelCatalog,
}: SubscriberSelectionInsightsModalProps) {
  if (!isOpen) return null;

  const insights = mockSelectionInsights(selectedRows);

  return (
    <div
      className="fixed inset-0 z-50 flex items-stretch sm:items-center justify-center bg-black/50 p-0 sm:p-4"
      role="presentation"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg rounded-xl bg-white shadow-xl dark:bg-gray-800"
        role="dialog"
        aria-modal="true"
        aria-labelledby="selection-insights-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-violet-600" aria-hidden />
            <h2 id="selection-insights-title" className="text-lg font-semibold text-gray-900 dark:text-white">
              Commonalities
            </h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-700"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="space-y-4 px-5 py-4">
          <span className="inline-flex rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-900 dark:bg-amber-900/40 dark:text-amber-200">
            Mock AI — embeddings coming later
          </span>

          <p className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">{insights.summary}</p>

          {insights.sharedChannels.length > 0 ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Shared channels
              </h3>
              <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
                {insights.sharedChannels.map((s) => channelLabel(s, channelCatalog)).join(', ')}
              </p>
            </div>
          ) : null}

          {Object.keys(insights.roleCounts).length > 0 ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Roles
              </h3>
              <ul className="mt-1 space-y-1 text-sm text-gray-800 dark:text-gray-200">
                {Object.entries(insights.roleCounts)
                  .sort((a, b) => b[1] - a[1])
                  .map(([role, count]) => (
                    <li key={role}>
                      {subscriberRoleLabel(role) || role}: {count}
                    </li>
                  ))}
              </ul>
            </div>
          ) : null}

          {insights.commonKeywords.length > 0 ? (
            <div>
              <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                Recurring themes
              </h3>
              <p className="mt-1 text-sm text-gray-800 dark:text-gray-200">
                {insights.commonKeywords.join(', ')}
              </p>
            </div>
          ) : null}
        </div>

        <div className="flex justify-end border-t border-gray-200 px-5 py-3 dark:border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg bg-emerald-600 px-4 py-2 text-sm font-medium text-white hover:bg-emerald-700"
          >
            Done
          </button>
        </div>
      </div>
    </div>
  );
}
